// RUNTIME CONFIGURATION for the demo host-app.
//
// ---------------------------------------------------------------------------
// ANSHIN ONLY. THERE IS NO BRAND PARAMETER, DELIBERATELY.
// ---------------------------------------------------------------------------
// An earlier version of this file resolved a `DEMO_BRAND` of `anshin | martlet` against
// a brand registry. That is gone: the Marti/Martlet side became a separate FORKED
// project, not a brand-pin of this image, so a `BRAND` env with a martlet branch would
// be a dead, unreachable path — the "built but unwired" defect this programme exists to
// remove. The brand registry module and its tests were deleted rather than disabled; a
// disabled path still has to be read, reviewed and kept compiling.
//
// ---------------------------------------------------------------------------
// WHY RUNTIME AND NOT BUILD-TIME
// ---------------------------------------------------------------------------
// A Vite bundle bakes `import.meta.env` at build time, so a Connect URL read that way
// would need a REBUILD per value — and the whole point of held-mode is that the real
// login is a CONFIG FLIP, not a rebuild. So the image is value-agnostic: the container
// entrypoint writes `/config.js` from its environment before nginx starts, `index.html`
// loads it before the bundle, and this module reads `window.__ANNA_DEMO_CONFIG__`.
//
// `import.meta.env` is still read, but ONLY as the local-dev source when no `/config.js`
// has been injected. Injected wins field by field, so a deployed pod can never be
// silently overridden by whatever was baked at build time.
//
// ---------------------------------------------------------------------------
// THERE IS NO CONFIGURED TENANT. THAT IS THE POINT.
// ---------------------------------------------------------------------------
// `DEMO_TENANT_ID` is gone too. The tenant the demo acts under comes from the
// authenticated user's AuthSession (`me.tenant.tenant_id`) and from nowhere else. A
// tenant in a values file is a scope claim the deployment invents, and it would travel
// into the launcher's `tenant` query param and into Anna's caller-context audit rows.
// With no configured tenant there is nothing to fall back TO, which is what makes the
// fail-closed behaviour structural rather than a rule someone has to remember.

import {
  ANNA_SDK_APP_KEY,
  DEFAULT_AUTH_BASE_PATH,
  DEFAULT_CALLBACK_PATH,
  normaliseConnectBaseUrl,
  type ConnectConfig,
} from './auth/gate';

/** Which Anna environment the launcher opens. Mirrors the SDK's `AnnaEnv`. */
export type DemoEnv = 'dev' | 'prod';

/** The shape the container entrypoint writes to `/config.js`. All values are strings. */
export interface InjectedDemoConfig {
  env?: string;
  baseUrl?: string;
  callerSrc?: string;
  imageTag?: string;
  connectBaseUrl?: string;
  authBasePath?: string;
  bridgeApiUrl?: string;
  held?: string;
}

declare global {
  interface Window {
    __ANNA_DEMO_CONFIG__?: InjectedDemoConfig;
  }
}

export interface DemoConfig {
  /** Which Anna environment the launcher targets. */
  env: DemoEnv;
  /**
   * The Anna product origin this demo treats as its backend, with NO trailing slash.
   *
   * The published SDK 0.7.x now HONOURS a `baseUrl` override (0.6.1 did not; see
   * `./sdk-capabilities.ts`, which probes this at runtime). But this demo still mounts the
   * launcher by `env` alone, so this field steers the calls the DEMO makes itself
   * (`prep-session`) and is the value the Target page shows for comparison — it does NOT
   * currently repoint the launcher. Passing it to `<AnnaLauncher baseUrl=…>` is a one-line
   * change the installed SDK now supports.
   */
  baseUrl: string;
  /** The brand-registry key the demo identifies itself as (`src` query param). */
  callerSrc: string;
  /** The container image tag, surfaced in the UI so a tester can say what they tested. */
  imageTag: string;
  /** Path A login configuration. */
  connect: ConnectConfig;
  /**
   * Where the browser POSTs the bridge — an ORIGIN, not a base path, and SEPARATE from
   * `connect.authBasePath`.
   *
   * `bridgeSession` builds `${apiUrl}/api/auth/session/bridge` itself, so the EMPTY
   * STRING is the correct same-origin value and yields `/api/auth/session/bridge`.
   * Passing `/api/auth` here produced `/api/auth/api/auth/session/bridge` — caught by the
   * callback Playwright spec. A consequence worth knowing: because the helper hardcodes
   * that path, customising `authBasePath` away from `/api/auth` moves `/validate` and
   * `/me` but NOT the bridge.
   *
   * The exchange code is bound to `(session_id, client_ip, sha256(user_agent))`. A
   * reverse-proxied bridge POST reaches Connect from the demo POD's IP rather than the
   * browser's, so the IP binding may not match unless Connect honours a forwarded-for
   * header — unverified from here. Keeping this independent means the bridge can go
   * direct while `/validate` and `/me` use the same-origin proxy, with no code change.
   */
  bridgeApiUrl: string;
  /** True when the operator has explicitly held this deployment's login. */
  heldByConfig: boolean;
  /** Null when no Connect origin is configured yet — which means held, not broken. */
  connectBaseUrlOrNull: string | null;
}

/** Raised for any configuration value the demo refuses to guess at. */
export class DemoConfigError extends Error {
  constructor(public readonly field: string, detail: string) {
    super(`demo config: ${field} — ${detail}`);
    this.name = 'DemoConfigError';
  }
}

/**
 * The default target: the deployed dev Anna product.
 *
 * The same origin the published SDK resolves for `dev` from its own brand profile
 * (`appUrls.dev`, verified against the installed bundle). They agree today;
 * `sdk-capabilities.ts` proves at runtime whether they still do rather than assuming it.
 */
export const DEFAULT_DEV_BASE_URL = 'https://anna.dev.anshinhealth.dev';

/** The brand-registry key this demo identifies itself as. */
export const DEFAULT_CALLER_SRC = 'anshin-anna-sdk-demo';

const ENVS: readonly string[] = ['dev', 'prod'];

/**
 * Normalise an origin, or throw.
 *
 * Rejects anything that is not an absolute `https:` URL and strips exactly one trailing
 * slash. The strip is not cosmetic: every consumer concatenates `${base}/api/...`, so a
 * configured `https://anna.dev.anshinhealth.dev/` produces `//api/caller/prep-session` —
 * a 404 whose cause is invisible in the UI and whose fix is one character in a values
 * file nobody re-reads.
 *
 * `http:` is refused rather than upgraded. Silently rewriting the scheme hides the bad
 * value, and these URLs carry the user's session.
 */
export function normaliseBaseUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) throw new DemoConfigError('baseUrl', 'empty');
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new DemoConfigError('baseUrl', `not a URL: ${JSON.stringify(trimmed)}`);
  }
  if (parsed.protocol !== 'https:') {
    throw new DemoConfigError(
      'baseUrl',
      `must be https: (got ${JSON.stringify(parsed.protocol)}); the prep-session call carries a session cookie`,
    );
  }
  if (parsed.pathname !== '/' && parsed.pathname !== '') {
    throw new DemoConfigError(
      'baseUrl',
      `must be an origin with no path (got ${JSON.stringify(parsed.pathname)})`,
    );
  }
  return parsed.origin;
}

/** Validate the env, or throw. Never defaults — `env` chooses which Anna a tester hits. */
export function parseDemoEnv(raw: string): DemoEnv {
  const v = raw.trim();
  if (!ENVS.includes(v)) {
    throw new DemoConfigError('env', `expected one of ${ENVS.join('|')}, got ${JSON.stringify(v)}`);
  }
  return v as DemoEnv;
}

/**
 * Parse a boolean-ish container variable.
 *
 * Only the listed spellings are true, and ANYTHING ELSE non-blank is an error rather
 * than false. `DEMO_HELD=flase` must not quietly mean "not held": held is the
 * fail-closed state, so a typo that silently disables it would un-hold a deployment
 * whose login is not wired, and the demo would then try to reach a Connect that is not
 * there and render an error instead of a held notice.
 */
export function parseHeld(raw: string | undefined): boolean {
  const v = (raw ?? '').trim().toLowerCase();
  if (v === '') return false;
  if (['1', 'true', 'yes', 'on'].includes(v)) return true;
  if (['0', 'false', 'no', 'off'].includes(v)) return false;
  throw new DemoConfigError('held', `expected a boolean, got ${JSON.stringify(raw)}`);
}

/**
 * Build the config from an already-gathered record of raw strings.
 *
 * Split from `loadDemoConfig()` so every branch is testable without a `window`, a built
 * bundle or an injected script.
 */
export function buildDemoConfig(raw: InjectedDemoConfig): DemoConfig {
  const env = parseDemoEnv(raw.env ?? 'dev');
  const baseUrl = normaliseBaseUrl(raw.baseUrl ?? DEFAULT_DEV_BASE_URL);
  const callerSrc = (raw.callerSrc ?? '').trim() || DEFAULT_CALLER_SRC;
  const imageTag = (raw.imageTag ?? '').trim() || 'unknown';
  const heldByConfig = parseHeld(raw.held);

  // A blank Connect URL is HELD, not an error — the real login flips in later. So it is
  // normalised only when present, and the null is carried through for `decideGate`.
  const rawConnect = (raw.connectBaseUrl ?? '').trim();
  const connectBaseUrlOrNull = rawConnect === '' ? null : normaliseConnectBaseUrl(rawConnect);

  const authBasePath = (raw.authBasePath ?? '').trim() || DEFAULT_AUTH_BASE_PATH;
  // Defaults to the same-origin proxy base, matching authBasePath, because that is the
  // only shape measured to work today. Overridable for the IP-binding reason above.
  const bridgeApiUrl = (raw.bridgeApiUrl ?? '').trim();

  return {
    env,
    baseUrl,
    callerSrc,
    imageTag,
    heldByConfig,
    connectBaseUrlOrNull,
    bridgeApiUrl,
    connect: {
      // `decideGate` never consults this when the origin is absent, and `buildLoginUrl`
      // throws on a blank one — so the empty string here is unreachable in the held
      // path rather than a silent default host.
      connectBaseUrl: connectBaseUrlOrNull ?? '',
      authBasePath,
      appKey: ANNA_SDK_APP_KEY,
      callbackPath: DEFAULT_CALLBACK_PATH,
    },
  };
}

/**
 * Gather raw config strings, injected-first.
 *
 * Exported for the test that pins the PRECEDENCE, which is the part that would rot
 * silently: if `import.meta.env` ever won, a deployed pod would serve whatever was baked
 * into the image and the values file would have no effect, with nothing failing.
 */
export function gatherRawConfig(
  injected: InjectedDemoConfig | undefined,
  buildTime: Record<string, string | undefined>,
): InjectedDemoConfig {
  const fromBuild: InjectedDemoConfig = {
    env: buildTime.VITE_DEMO_ENV,
    baseUrl: buildTime.VITE_DEMO_BASE_URL,
    callerSrc: buildTime.VITE_DEMO_CALLER_SRC,
    imageTag: buildTime.VITE_DEMO_IMAGE_TAG,
    connectBaseUrl: buildTime.VITE_DEMO_CONNECT_BASE_URL,
    authBasePath: buildTime.VITE_DEMO_AUTH_BASE_PATH,
    bridgeApiUrl: buildTime.VITE_DEMO_BRIDGE_API_URL,
    held: buildTime.VITE_DEMO_HELD,
  };
  return {
    env: pick(injected?.env, fromBuild.env),
    baseUrl: pick(injected?.baseUrl, fromBuild.baseUrl),
    callerSrc: pick(injected?.callerSrc, fromBuild.callerSrc),
    imageTag: pick(injected?.imageTag, fromBuild.imageTag),
    connectBaseUrl: pick(injected?.connectBaseUrl, fromBuild.connectBaseUrl),
    authBasePath: pick(injected?.authBasePath, fromBuild.authBasePath),
    bridgeApiUrl: pick(injected?.bridgeApiUrl, fromBuild.bridgeApiUrl),
    held: pick(injected?.held, fromBuild.held),
  };
}

/**
 * First non-blank of the two.
 *
 * A blank string is treated as absent on purpose: the entrypoint writes every key
 * unconditionally, so an unset container variable arrives as `""`. Treating `""` as
 * present would make an unset `DEMO_ENV` throw a config error naming the empty string,
 * which an operator cannot act on, instead of falling through to the documented default.
 */
function pick(a: string | undefined, b: string | undefined): string | undefined {
  if (typeof a === 'string' && a.trim() !== '') return a;
  if (typeof b === 'string' && b.trim() !== '') return b;
  return undefined;
}

/** Read the live configuration. Throws `DemoConfigError` / `ConnectConfigError`. */
export function loadDemoConfig(): DemoConfig {
  const injected = typeof window === 'undefined' ? undefined : window.__ANNA_DEMO_CONFIG__;
  const buildTime = (import.meta.env ?? {}) as unknown as Record<string, string | undefined>;
  return buildDemoConfig(gatherRawConfig(injected, buildTime));
}
